import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Badge } from "./ui/badge";

/**
 * Renderiza um único campo de pergunta customizada no formulário de RDO.
 * Suporta tipos: text | number | time | yes_no | select.
 * Persiste em formData.custom_answers[question.id] via onChange(value).
 */
const CustomQuestionField = ({ question, value, onChange, error }) => {
  const id = `cq-${question.id}`;
  const hasError = !!error;
  const cls = hasError ? "border-red-500" : "";
  const testid = `cq-field-${question.id}`;

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3 space-y-2" data-testid={`cq-card-${question.id}`}>
      <div className="flex items-start justify-between gap-2">
        <Label htmlFor={id} className="font-medium text-slate-800">
          {question.label}
          {question.is_required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700">Pergunta customizada</Badge>
      </div>

      {question.description && (
        <p className="text-xs text-slate-500">{question.description}</p>
      )}

      {question.type === "text" && (
        <Textarea
          id={id}
          rows={2}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={testid}
        />
      )}

      {question.type === "number" && (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="number"
            step="any"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
            className={`${cls} max-w-[200px]`}
            data-testid={testid}
          />
          {question.unit && <span className="text-sm text-slate-500">{question.unit}</span>}
        </div>
      )}

      {question.type === "time" && (
        <Input
          id={id}
          type="time"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${cls} max-w-[160px]`}
          data-testid={testid}
        />
      )}

      {question.type === "yes_no" && (
        <RadioGroup
          value={value === true ? "yes" : value === false ? "no" : ""}
          onValueChange={(v) => onChange(v === "yes")}
          className="flex gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-yes`} value="yes" data-testid={`${testid}-yes`} />
            <Label htmlFor={`${id}-yes`}>Sim</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id={`${id}-no`} value="no" data-testid={`${testid}-no`} />
            <Label htmlFor={`${id}-no`}>Não</Label>
          </div>
        </RadioGroup>
      )}

      {question.type === "select" && (
        <Select value={value || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className={`${cls} max-w-md`} data-testid={testid}>
            <SelectValue placeholder="Selecione…" />
          </SelectTrigger>
          <SelectContent>
            {(question.select_options || []).map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {hasError && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

/**
 * Renderiza todas as perguntas customizadas de uma seção específica.
 * Filtra automaticamente por target_section + is_active.
 */
export const CustomQuestionsSection = ({ section, questions, answers, onAnswerChange, errors = {} }) => {
  const filtered = (questions || []).filter(q => q.is_active !== false && q.target_section === section);
  if (filtered.length === 0) return null;
  return (
    <div className="space-y-3 mt-4 border-t pt-4" data-testid={`cq-section-${section}`}>
      <h4 className="text-sm font-semibold text-slate-700">📋 Perguntas customizadas</h4>
      {filtered.map(q => (
        <CustomQuestionField
          key={q.id}
          question={q}
          value={answers?.[q.id]}
          onChange={(v) => onAnswerChange(q.id, v)}
          error={errors[q.id]}
        />
      ))}
    </div>
  );
};

export default CustomQuestionField;
